<template>
  <div>
    <h2 class="step-name">Current Mastitis Situation</h2>
    <div class="step-section">
      <h3>Historic BMSCC</h3>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Shed/Herd</th>
            <th>Season</th>
            <th>Average BMSCC</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(bmscc, index) in getChartData('dc2_bmscc_table')" :key="index">
            <td class="text-left">{{ bmscc["Shed/Herd"] }}</td>
            <td class="text-left">{{ bmscc["Season"] }}</td>
            <td class="text-left">{{ bmscc["Average BMSCC"] }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="step-section">
      <div class="clearfix" :style="{ width: 400 + widthForRow(getFieldValue('FARMS')) + 'px' }">

        <div v-for="farm in getFieldValue('FARMS')" class="farm-fields" :key="farm.id">
          <h3>
            <span class="form-group">
              {{ farm.farm_label }}
              <comments :field-name="`farm_${farm.id}-season_avg_bmscc`" :isReadOnly="isReadOnly">
              </comments>
            </span>
          </h3>

          <number-input label="Season Avg BMSCC"
                        :field-name="`farm_${farm.id}-season_avg_bmscc`"
                        :disabled="isReadOnly">
          </number-input>

          <number-input label="Number of cell count grades"
                        :field-name="`farm_${farm.id}-num_cell_count_grades`"
                        :disabled="isReadOnly">
          </number-input>

        </div>
      </div>

    </div>

    <div v-for="farm in getFieldValue('FARMS')" :key="farm.id">
      <div class="step-section">
        <h3>
          <span class="form-group">
            Mastitis Treatments ({{ farm.farm_label }})
            <comments :field-name="`farm_${farm.id}-chart_mastitis_treatments`"
                :isReadOnly="isReadOnly">
            </comments>
          </span>
        </h3>
        <p>Estimated number of cows treated per month based on purchases.</p>

        <bar :chart-data="treatmentsChartData(farm.id)" :options="treatmentsChartOptions()" ></bar>

        <table class="table table-bordered">
          <thead>
            <tr>
              <th></th>
              <th v-for="(date, index) in chartMastitisTreatmentsDates(farm.id)" :key="index"> {{ date }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, productIndex) in chartMastitisTreatmentsProducts(farm.id)" :key="productIndex">
              <td>{{ product }}</td>
              <td v-for="(date, dateIndex) in chartMastitisTreatmentsDates(farm.id)" :key="dateIndex">
                {{ getProductValueForDate(farm.id, product, date) || '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="step-section">
      <h3>
        <span class="form-group">
          Clinical Cases
          <comments field-name="heading_clinical_cases"
                    :isReadOnly="isReadOnly">
          </comments>
        </span>
      </h3>
      <p> Estimated number of cows treated during different lactation
      periods as a percentage of total cows wintered.</p>
      <div v-for="farm in getFieldValue('FARMS')" :key="farm.id">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>{{farm.farm_label}}</th>
              <th>Percent Cows Treated</th>
              <th>Percent cows treated region average </th>
              <th>Goal</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(caseData, index) in getChartData('dc_mastitis_clinical_treatments_summ')[farm.id]" :key="index">
              <td class="text-left">
                {{caseData['Period']}}
              </td>
              <td class="text-left">
                {{caseData['Percent Cows Treated']}}
              </td>
              <td class="text-left">
                {{caseData['Region Percent Cows Treated']}}
              </td>
              <td class="text-left">
                {{caseData['Goal']}}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="step-section">
      <h3>
        <span class="form-group">
          Milk culture - Mastatest results
          <comments field-name="heading_mastatest_results"
                    :isReadOnly="isReadOnly">
          </comments>
        </span>
      </h3>
      <div class="step-section" v-for="(farmResult, index) in getChartData('dc_mastatest_results')" :key="'farm_ma_result_'+index">
        <h3>{{ farmResult.farm_label }}</h3>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>In-house</th>
              <th>Cow number</th>
              <th>Quarter</th>
              <th>Pathogens present</th>
              <th>Antibiotic susceptibility</th>
              <th>Recommendation/Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in farmResult.result" :key="'farm_ma_row_'+index">

              <td class="text-left">{{ row["Date"] }}</td>
              <td class="text-left">{{ row["In-house"] }}</td>
              <td class="text-left">{{ row["Cow number"] }}</td>
              <td class="text-left">{{ row["Quarter"] }}</td>
              <td class="text-left" v-html="commaSeparatedValue(row['Pathogens present'])"></td>
              <td class="text-left" v-html="commaSeparatedValue(row['Antibiotic susceptibility'])"></td>
              <td class="text-left">{{ row["Recommendation/Comments"] }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="step-section">
      <h3>
        <span class="form-group">
          Milk culture - Gribbles results
          <comments field-name="heading_gribbles_results"
                    :isReadOnly="isReadOnly">
          </comments>
        </span>
      </h3>
      <div class="step-section" v-for="(farmResult, index) in getChartData('dc_gribbles_results')" :key="'farm_gri_result_'+index">
        <h3>{{ farmResult.farm_label }}</h3>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Test</th>
              <th>Results</th>
              <th>Antibiotic sensitivity</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in farmResult.result" :key="'farm_gri_row_'+index">
              <td class="text-left">{{ row["Date"] }}</td>
              <td class="text-left">{{ row["Test"] }}</td>
              <td class="text-left" v-html="commaSeparatedValue(row['Results'])"></td>
              <td class="text-left" v-html="commaSeparatedValue(row['Antibiotic sensitivity'])"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="step-section">
      <h3>
        <span class="form-group">
          Calving Mastitis
          <comments field-name="heading_calving_mastitis"
                    :isReadOnly="isReadOnly">
          </comments>
        </span>
      </h3>
      <div class="clearfix" :style="{ width: 400 + widthForRow(getFieldValue('FARMS')) + 'px' }">
        <div v-for="farm in getFieldValue('FARMS')" class="farm-fields" :key="farm.id">
          <div>
            <h3>{{ farm.farm_label }}</h3>
            <div class="alert alert-warning" v-if="totalCowsTreated(farm.id) == 0">
              According to sales data there were no cows treated. Please contact farm services for more information
            </div>
            <div class="form-group">
              <label for="notes">
                Of the {{totalCowsTreated(farm.id)}} cows treated, what
                <span class="alert-danger">PERCENTAGE</span> were heifers?
              </label>

              <number-input label=""
                            :field-name="`farm_${farm.id}-est_percent_mastitis_treat_is_heifers`"
                            v-validate="'between:0,100'"
                            data-vv-as="Estimate % of Heifers"
                            :disabled="totalCowsTreated(farm.id) == 0 || isReadOnly"
                            @on-change="updateCalvingMastitisValues(farm.id, 'percent')">
              </number-input>

            </div>

            <p class="text-danger">
            <strong>OR</strong>
            </p>

            <div class="form-group">
              <label for="notes">
                Of the {{totalCowsTreated(farm.id)}} cows treated, what
                <span class="alert-danger">NUMBER</span> were heifers?
              </label>

              <number-input label=""
                            :field-name="`farm_${farm.id}-est_number_mastitis_treat_is_heifers`"
                            v-validate="'between:0,100'"
                            data-vv-as="Estimate % of Heifers"
                            :disabled="totalCowsTreated(farm.id) == 0 || isReadOnly"
                            @on-change="updateCalvingMastitisValues(farm.id, 'number')">
              </number-input>
            </div>
          </div>
          <div class="step-section">
            <div class="form-group">
              <label for="notes">Estimated % of treatments that were mixed age</label>
              <input type="number"
                     class="form-control"
                     :value="estPercentMastitisTreatMixed(farm.id)"
                     :disabled="true">
            </div>
          </div>
        </div>
      </div>
      <br>

      <div class="">

        <h3>
          Percentage of animal group treated for Mastitis
        </h3>
        <div v-for="farm in getFieldValue('FARMS')" :key="farm.id">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>{{farm.farm_label}}</th>
                <th>Animal Numbers</th>
                <th>Number treated in spring previous season</th>
                <th>Number treated in spring</th>
                <th>Percent of group treated</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>total cows wintered</td>
                <td>{{totalCowsWintered(farm.id)}}</td>
                <td>
                  {{(getChartData('dc_spring_mastitis_breakdown')[farm.id][0] || {'prev_tot': 0}).prev_tot}}
                </td>
                <td>
                  {{totalCowsTreated(farm.id)}}
                </td>
                <td>
                  {{(getChartData('dc_spring_mastitis_breakdown')[farm.id][0] || {'percent_treated': 0}).percent_treated}}
                </td>
              </tr>
              <tr>
                <td>Mixed Age</td>
                <td>
                  {{(getChartData('dc_spring_mastitis_breakdown')[farm.id][0] || {'mixed_age': 0}).mixed_age}}
                </td>
                <td>
                  {{(getChartData('dc_spring_mastitis_breakdown')[farm.id][0] || {'prev_ma': 0}).prev_ma}}
                </td>
                <td>
                  {{mixedAgeTreated(farm.id)}}
                </td>
                <td>
                  {{mixedAgePercentTreated(farm.id)}}
                </td>
              </tr>
              <tr>
                <td>Heifers</td>
                <td>
                  {{(getChartData('dc_spring_mastitis_breakdown')[farm.id][0] || {'heifers': 0}).heifers}}
                </td>
                <td>
                  {{(getChartData('dc_spring_mastitis_breakdown')[farm.id][0] || {'prev_hef': 0}).prev_hef}}
                </td>
                <td>
                  {{heifersTreated(farm.id)}}
                </td>
                <td>
                  {{heifersPercentTreated(farm.id)}}
                </td>
              </tr>
            </tbody>
          </table>

        </div>
      </div>
    </div>

    <div class="step-section">
      <div class="clearfix" :style="{ width: 400 + widthForRow(getFieldValue('FARMS')) + 'px' }">
        <div v-for="farm in getFieldValue('FARMS')"  class="farm-fields" :key="farm.id">
          <h3>{{farm.farm_label}}</h3>
          <div class="form-group">
            <label for="notes">Notes on treatment protocols</label>
            <text-area label=""
                       :field-name="`farm_${farm.id}-last_year_treatment_protocols_notes`"
                       disabled>
            </text-area>

            <text-area label=""
                       :field-name="`farm_${farm.id}-treatment_protocols_notes`"
                       :disabled="isReadOnly">
            </text-area>
          </div>

          <div class="form-group">
            <label for="notes">Milk quality key points to be revised at winter consult</label>
            <text-area label=""
                       :field-name="`farm_${farm.id}-last_year_milk_quality_key_points_for_wc`"
                       disabled>
            </text-area>
          </div>
          <text-area label=""
                     :field-name="`farm_${farm.id}-milk_quality_key_points_for_wc`"
                     :disabled="isReadOnly">
          </text-area>
        </div>
      </div>
    </div>

    <drug-allocation-cart :farms="getFieldValue('FARMS')" :drugs="getFieldValue('DRUGS')"></drug-allocation-cart>
  </div>
</template>

<script>
import DrugAllocationCart from './current_mastitis_situation/DrugAllocationCart'
import baseComponent from '@/mixins/base_consult_component_v2'
import Bar from '../charts/Bar'

export default {
  name: 'CurrentMastitisSituation',
  mixins: [ baseComponent ],
  components: {
    Bar,
    DrugAllocationCart,
  },
  methods: {
    commaSeparatedValue: function(str) {
      let text = ''
      let splittedText = (str || '').split(', ')

      if (!(splittedText.length == 1 && splittedText[0] == '')) {
        splittedText.forEach(function(item) {
          text += '<p>- '+ item +'</p>'
        })
      }
      return text
    },
    totalCowsTreated: function (farmId) {
      if (this.getChartData('dc_spring_mastitis_breakdown')[farmId][0]) {
        return this.getChartData('dc_spring_mastitis_breakdown')[farmId][0].cows_treated
      }
      return 0
    },
    totalCowsWintered: function (farmId){
      if (this.getChartData('dc_spring_mastitis_breakdown')[farmId][0]) {
        return this.getChartData('dc_spring_mastitis_breakdown')[farmId][0].cows_wintered_incl_r2
      }
      return 0
    },
    estPercentMastitisTreatMixed: function(farmId) {
      if (this.getFieldValue(`farm_${farmId}-est_percent_mastitis_treat_is_heifers`, 'CurrentMastitisSituation')) {
        return 100 - this.getFieldValue(`farm_${farmId}-est_percent_mastitis_treat_is_heifers`, 'CurrentMastitisSituation')
      }
      return 0
    },
    mixedAgeTreated: function(farmId) {
      let cowsTreated = this.totalCowsTreated(farmId)

      return Math.round(this.estPercentMastitisTreatMixed(farmId) / 100 * cowsTreated, 1)
    },
    mixedAgePercentTreated: function(farmId) {
      let mixedAge

      if (this.getChartData('dc_spring_mastitis_breakdown')[farmId][0]) {
        mixedAge = this.getChartData('dc_spring_mastitis_breakdown')[farmId][0]['mixed_age']
      } else {
        mixedAge = 0
      }

      return Math.round(this.mixedAgeTreated(farmId) / mixedAge * 100, 1)
    },
    heifersTreated: function(farmId) {
      let cowsTreated

      if (this.getChartData('dc_spring_mastitis_breakdown')[farmId][0]) {
        cowsTreated = this.getChartData('dc_spring_mastitis_breakdown')[farmId][0]["cows_treated"]
      } else {
        cowsTreated = 0
      }

      return Math.round(this.getFieldValue(`farm_${farmId}-est_percent_mastitis_treat_is_heifers`, 'CurrentMastitisSituation') / 100 * cowsTreated, 1)
    },
    heifersPercentTreated: function(farmId) {
      let heifers

      if (this.getChartData('dc_spring_mastitis_breakdown')[farmId][0]) {
        heifers = this.getChartData('dc_spring_mastitis_breakdown')[farmId][0]["heifers"]
      } else {
        heifers = 0
      }
      return Math.round(this.heifersTreated(farmId) / heifers * 100, 1)
    },
    updateCalvingMastitisValues: function (farmId, inputType) {
      let cowsTreated = this.totalCowsTreated(farmId)

      if (inputType == 'percent') {
        let fixed = (this.getFieldValue(`farm_${farmId}-est_percent_mastitis_treat_is_heifers`, 'CurrentMastitisSituation') * cowsTreated)/100
        this.setFieldValue({
          fieldName: `farm_${farmId}-est_number_mastitis_treat_is_heifers`,
          value: Math.round(fixed, 1),
        })
      } else if (inputType == 'number') {
        let percent = (this.getFieldValue(`farm_${farmId}-est_number_mastitis_treat_is_heifers`, 'CurrentMastitisSituation') * 100)/cowsTreated
        this.setFieldValue({
          fieldName: `farm_${farmId}-est_percent_mastitis_treat_is_heifers`,
          value: Math.round(percent, 1)
        })
      }

      this.setFieldValue({
        fieldName: `farm_${farmId}-mastitis_treated_spring_total`,
        value: cowsTreated,
      })
      this.setFieldValue({
        fieldName: `farm_${farmId}-mastitis_treated_spring_ma`,
        value: this.mixedAgeTreated(farmId)
      })
      this.setFieldValue({
        fieldName: `farm_${farmId}-mastitis_treated_spring_ma`,
        value: this.heifersTreated(farmId)
      })
    },
    chartMastitisTreatmentsDates: function(farmId) {
      return Array.from(new Set(this.getChartData('dc_mastitis_treatments')[farmId].map(x => x['Date']))).reverse()
    },
    chartMastitisTreatmentsProducts: function(farmId) {
      return Array.from(new Set(this.getChartData('dc_mastitis_treatments')[farmId].filter(x => x['Product']).map(x => x['Product'])))
    },
    treatmentsChartData: function(farmId) {
      var self = this;

      var dates  = this.chartMastitisTreatmentsDates(farmId)
      var products = this.chartMastitisTreatmentsProducts(farmId)

      var datasets = []

      products.forEach(function(productName) {
        var product = {
          label: productName,
          backgroundColor: '#'+(Math.random()*0xFFFFFF<<0).toString(16),
          data: []
        }
        dates.forEach(function(dateName){
          product['data'].push(self.getProductValueForDate(farmId, productName, dateName))
        })

        datasets.push(product)
      })

      return {
        labels: dates,
        datasets: datasets
      }
    },
    getProductValueForDate: function(farmId, product, dateName) {
      var value = this.getChartData('dc_mastitis_treatments')[farmId].filter(x => x['Date'] == dateName && x['Product'] == product)
      if (value.length > 0) {
        return value[0]['Cows Treated'];
      }
      return 0
    },
    treatmentsChartOptions: function() {
      return {
        responsive: true,
        scales: {
          xAxes: [{
            stacked: true,
            categoryPercentage: 0.5,
            barPercentage: 1
          }],
          yAxes: [{
            stacked: true
          }]
        }
      }
    },
  },
};
</script>
